import {useMemo} from 'react';
import {twJoin} from 'tailwind-merge';
import {IconArrowRightDashed} from '@tabler/icons-react';
import {AnimatePresence, m as motion} from 'motion/react';
import {Dialog, Modal, ModalOverlay} from 'react-aria-components';
import getRandomInteger from 'server/utils/getRandomInteger';
import ImageDescription from '../ImageDescription';
import {caveat} from 'src/utils/fonts';
import BaseButton from '../BaseButton';
import RemixIcon from '../RemixIcon';

const messages = {
  correct: ['Good job!', 'Got it!', 'Nailed it.', 'Yes!', 'Correct!', 'Nice!', 'Well done!'],
  incorrect: ['Not quite.', 'Yeah no.', 'Nope.', 'Wrong.']
};

// @ts-ignore
const AnimatedModal = motion.create(Modal);

type Props = {
  isOpen: boolean;
  isLastQuestion: boolean;
  onOpenChange: (isOpen: boolean) => void;
  overlayStyle?: React.CSSProperties;
  explanation: string;
  isCorrect: boolean;
  id: string;
  image: PlaceImage | undefined;
};

const QuizPostAnswerModal = ({
  id,
  isOpen,
  onOpenChange,
  overlayStyle,
  explanation,
  isLastQuestion,
  isCorrect,
  image
}: Props) => {
  const message = useMemo(() => {
    return isCorrect
      ? messages.correct[getRandomInteger(0, messages.correct.length - 1)]
      : messages.incorrect[getRandomInteger(0, messages.incorrect.length - 1)];
  }, [id, isCorrect]);

  return (
    <AnimatePresence>
      {isOpen ? (
        <ModalOverlay
          key="overlay"
          isDismissable={false}
          isKeyboardDismissDisabled
          isOpen
          onOpenChange={onOpenChange}
          style={overlayStyle}
          className="fixed top-0 h-(--visual-viewport-height) right-0 left-0 z-20 flex flex-col items-center justify-end pb-4"
        >
          <AnimatedModal
            initial={{opacity: 0, y: '80%'}}
            animate={{opacity: 1, y: '0%', transition: {delay: 1.2}}}
            exit={{opacity: 1, y: '120%'}}
            transition={{type: 'spring', duration: 0.6, bounce: 0.2}}
            className={twJoin(
              'relative z-21 will-change-transform bg-(--dark-quiz-bg-color) w-[500px] max-w-[calc(100%-20px)] shadow-[inset_0_0_1px_1px_rgb(255_255_255_/_0.2),0_0_0_1px_rgb(23_10_37_/_0.1),0_3px_20px_rgb(23_10_37_/_0.1),0_30px_80px_rgb(23_10_37_/_0.4)] px-4 pb-8 max-[500px]:pb-4 pt-6 rounded-b-[48px] max-[500px]:rounded-b-[32px]',
              explanation ? 'rounded-t-xl' : 'rounded-t-[48px]'
            )}
          >
            <Dialog aria-label="Go to next question" className="text-white gap-2 flex flex-col mx-3 max-[500px]:mx-0">
              {({close}) => (
                <>
                  <div
                    className={twJoin(
                      caveat.className,
                      'text-3xl text-(--dark-quiz-text-color) opacity-80 mb-3 text-center'
                    )}
                  >
                    {message}
                  </div>

                  {explanation ? (
                    <div className="bg-(--button-bg-hover) rounded-lg p-4 shadow inset-shadow-[0_1px_1px_rgb(255_255_255_/_0.05)] mb-1">
                      <p className="text-lg max-[600px]:text-base leading-normal">{explanation}</p>
                    </div>
                  ) : null}

                  {image ? (
                    <div className="text-(--dark-quiz-text-color) opacity-90 mb-4 text-sm">
                      <ImageDescription image={image} showImageDescription={false} />
                    </div>
                  ) : null}

                  <BaseButton
                    onPress={close}
                    autoFocus
                    type="button"
                    color="#252525"
                    className="bg-white justify-between data-focus-visible:ring-5 data-focus-visible:ring-[rgba(255,255,255,0.4)] data-[pressed]:brightness-75"
                    size="large"
                  >
                    <span className="text-lg font-heading font-medium">
                      {isLastQuestion ? 'Finish' : 'Next question'}
                    </span>

                    <RemixIcon TablerIcon={IconArrowRightDashed} color="#252525" />
                  </BaseButton>
                </>
              )}
            </Dialog>
          </AnimatedModal>
        </ModalOverlay>
      ) : null}
    </AnimatePresence>
  );
};

export default QuizPostAnswerModal;
